import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../components/Layout';
import UsStateMap, { normalizeStateCode, OWNER_COLOR_PALETTE, ViewMode, TerritoryOwner } from '../components/UsStateMap';
import UsCountyMap, { findCountyForCoords } from '../components/UsCountyMap';
import { feature as topoFeature } from 'topojson-client';

// LocalStorage-backed ZIP → {lat,lng} cache so we don't re-fetch zippopotam
// for the same dealers on every render / page reload.
const ZIP_CACHE_KEY = 'dealer_zip_geo_cache_v1';
function loadZipCache(): Record<string, { lat: number; lng: number } | null> {
  try { return JSON.parse(localStorage.getItem(ZIP_CACHE_KEY) || '{}'); }
  catch { return {}; }
}
function saveZipCache(cache: Record<string, { lat: number; lng: number } | null>) {
  try { localStorage.setItem(ZIP_CACHE_KEY, JSON.stringify(cache)); } catch {}
}
async function geocodeZip(zip: string): Promise<{ lat: number; lng: number } | null> {
  // zippopotam.us is free, no key needed. US-only. Returns 404 for invalid.
  try {
    const r = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (!r.ok) return null;
    const data = await r.json();
    const p = data.places?.[0];
    if (!p) return null;
    return { lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) };
  } catch { return null; }
}
import { api } from '../services/api';
import { Dealer } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface SalesRep {
  id: string;
  first_name: string;
  last_name: string;
  employee_id?: string;
  email: string;
}

// API helper
async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('admin_auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  return response.json();
}

export default function DealersPage() {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [salesRepFilter, setSalesRepFilter] = useState<string>('');
  const [flatRateFilter, setFlatRateFilter] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<string>('');
  const [repFirmFilter, setRepFirmFilter] = useState<string>('');
  const [showMap, setShowMap] = useState<boolean>(true);
  const [mapMode, setMapMode] = useState<ViewMode>('density');
  // When set, the map shows county-level detail for this state. Clicking a
  // state on the dealer-density map zooms in; "Back to US" clears.
  const [zoomState, setZoomState] = useState<string>('');
  // Per-dealer county FIPS (built lazily from ZIP via zippopotam.us +
  // point-in-polygon against the county topojson). Cached across renders.
  const [dealerFips, setDealerFips] = useState<Record<string, string>>({});
  const [countyFilter, setCountyFilter] = useState<{ fips: string; name: string } | null>(null);
  const [geocodingState, setGeocodingState] = useState<string>('');
  const [territoryCoverage, setTerritoryCoverage] = useState<{
    repFirms: { id: string; name: string; stateCodes: string[] }[];
    salesReps: { id: string; name: string; stateCodes: string[] }[];
  }>({ repFirms: [], salesReps: [] });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editingSalesRep, setEditingSalesRep] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [editingCreditLimit, setEditingCreditLimit] = useState<string | null>(null);
  const [creditLimitValue, setCreditLimitValue] = useState<number>(0);
  const [showCreditModal, setShowCreditModal] = useState<string | null>(null);
  const [creditSettings, setCreditSettings] = useState<any>(null);
  const [creditNetDays, setCreditNetDays] = useState<number>(30);
  const [creditEnabled, setCreditEnabled] = useState(false);
  const [creditLoadingId, setCreditLoadingId] = useState<string | null>(null);
  const [creditError, setCreditError] = useState<string | null>(null);

  // Starter Kit Credit state
  const [starterKitStatus, setStarterKitStatus] = useState<any>(null);
  const [starterKitLoading, setStarterKitLoading] = useState(false);

  // Branding state
  const [showBrandingModal, setShowBrandingModal] = useState<string | null>(null);
  const [brandingEmail, setBrandingEmail] = useState('');
  const [brandingLoading, setBrandingLoading] = useState(false);

  // Contact card state
  const [contactCardDealer, setContactCardDealer] = useState<any | null>(null);
  const [dealerUserCount, setDealerUserCount] = useState<number>(0);

  // Account type tab
  const [accountTypeTab, setAccountTypeTab] = useState<'all' | 'house' | 'rep' | 'direct'>('all');

  // Rep firm conversion modal
  const [showRepFirmModal, setShowRepFirmModal] = useState<string | null>(null);
  const [repFirms, setRepFirms] = useState<any[]>([]);
  const [selectedRepFirm, setSelectedRepFirm] = useState<string>('');

  // Impersonate dropdown state
  const [impersonateMenu, setImpersonateMenu] = useState<string | null>(null);
  const [teamData, setTeamData] = useState<{ contacts: any[]; salesReps: any[] } | null>(null);
  const [teamLoading, setTeamLoading] = useState(false);

  // Close impersonate menu on outside click
  useEffect(() => {
    if (!impersonateMenu) return;
    const handler = () => { setImpersonateMenu(null); setTeamData(null); };
    const timer = setTimeout(() => document.addEventListener('click', handler), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', handler); };
  }, [impersonateMenu]);

  useEffect(() => {
    loadDealers();
    loadSalesReps();
    loadDealerUserCount();
    loadRepFirms();
    loadTerritoryCoverage();
  }, [statusFilter]);

  // Clear rep-firm filter when switching away from the Rep Firm tab so a
  // hidden filter doesn't silently scope the list on other tabs.
  useEffect(() => {
    if (accountTypeTab !== 'rep' && repFirmFilter) setRepFirmFilter('');
  }, [accountTypeTab, repFirmFilter]);

  // When user zooms into a state, geocode that state's dealers (ZIP→lat/lng
  // via zippopotam.us, cached in localStorage) and assign each to a county
  // FIPS via point-in-polygon. Results live in dealerFips and drive both the
  // county density tooltip and the click-to-filter-list behavior.
  useEffect(() => {
    if (!zoomState) { setCountyFilter(null); return; }
    let cancelled = false;
    (async () => {
      setGeocodingState(zoomState);
      const cache = loadZipCache();
      // Load county features for point-in-polygon
      const topo = await fetch('/us-counties-10m.json').then(r => r.json());
      const fc = topoFeature(topo, topo.objects.counties) as any;
      const features = fc.features || [];

      const inState = dealers.filter(d => normalizeStateCode(d.address?.state || (d as any).state) === zoomState);
      const uniqueZips = Array.from(new Set(inState
        .map(d => d.address?.zip || (d as any).zip || (d as any).address_zip || '')
        .map(z => String(z).trim().slice(0, 5))
        .filter(z => /^\d{5}$/.test(z))));

      // Geocode any uncached ZIPs (parallel, throttled to 5 at a time)
      const uncached = uniqueZips.filter(z => !(z in cache));
      const concurrency = 5;
      for (let i = 0; i < uncached.length; i += concurrency) {
        if (cancelled) return;
        const batch = uncached.slice(i, i + concurrency);
        const results = await Promise.all(batch.map(z => geocodeZip(z).then(r => [z, r] as const)));
        for (const [z, r] of results) cache[z] = r;
      }
      saveZipCache(cache);
      if (cancelled) return;

      // Build dealer → FIPS map for in-state dealers
      const next: Record<string, string> = { ...dealerFips };
      for (const d of inState) {
        const z = String(d.address?.zip || (d as any).zip || (d as any).address_zip || '').trim().slice(0, 5);
        const geo = cache[z];
        if (!geo) continue;
        const county = findCountyForCoords(features, geo.lng, geo.lat);
        if (county) next[d.id] = county.fips;
      }
      setDealerFips(next);
      setGeocodingState('');
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomState, dealers.length]);

  const loadRepFirms = async () => {
    try {
      const data = await apiRequest('/rep-firms');
      setRepFirms(data || []);
    } catch (err) { console.error(err); }
  };

  const loadTerritoryCoverage = async () => {
    try {
      const data = await apiRequest('/dealers/territory-coverage');
      setTerritoryCoverage({
        repFirms: data?.repFirms || [],
        salesReps: data?.salesReps || [],
      });
    } catch (err) { console.error('Failed to load territory coverage:', err); }
  };

  const loadDealerUserCount = async () => {
    try {
      const res = await apiRequest('/users/dealer-portal?limit=1');
      setDealerUserCount(res?.pagination?.total || res?.total || 0);
    } catch (error) {
      console.error('Failed to load dealer user count:', error);
    }
  };

  const loadSalesReps = async () => {
    try {
      const reps = await apiRequest('/users/sales-rep/active');
      setSalesReps(reps || []);
    } catch (error) {
      console.error('Failed to load sales reps:', error);
    }
  };

  const loadDealers = async () => {
    try {
      setLoading(true);
      const response = await api.dealers.getDealers({ status: statusFilter || undefined, limit: 500 });
      // Map API response to expected format (API returns { dealers: [...], pagination: {...} })
      const mappedDealers = (response.dealers || response.data || response.items || []).map((dealer: any) => ({
        ...dealer,
        // Map field names
        name: dealer.name || dealer.company_name || dealer.companyName || dealer.company || 'Unknown',
        email: dealer.email || dealer.contact_email || '',
        phone: dealer.phone || '',
        status: dealer.status || 'active',
        discountPercent: dealer.discountPercent || dealer.discount_percent || 0,
        paymentMethod: dealer.paymentMethod || dealer.payment_method || 'Default',
        address: dealer.address || {
          street: dealer.street || '',
          city: dealer.city || '',
          state: dealer.state || '',
          zip: dealer.zip || '',
        },
        contact: dealer.contact || null,
        createdAt: dealer.createdAt || dealer.created_at,
        totalOrders: dealer.totalOrders || dealer.total_orders || 0,
        totalRevenue: dealer.totalRevenue || dealer.total_revenue || 0,
        sales_rep_id: dealer.salesRepId || dealer.sales_rep_id,
        sales_rep_name: dealer.salesRepName || dealer.sales_rep_name,
        credit_limit: dealer.credit_limit || 0,
        credit_balance: dealer.credit_balance || 0,
        rewards: dealer.rewards,
        branded_email: dealer.branded_email || null,
        branded_email_verified: dealer.branded_email_verified || false,
        logo_url: dealer.logo_url || null,
        starter_kit_purchased_at: dealer.starter_kit_purchased_at || null,
        starter_kit_credit_issued_at: dealer.starter_kit_credit_issued_at || null,
      }));
      setDealers(mappedDealers);
    } catch (error) {
      console.error('Failed to load dealers:', error);
      // Show empty state instead of mock data
      setDealers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (dealerId: string, newStatus: 'active' | 'inactive' | 'pending') => {
    try {
      setActionLoading(dealerId);
      await api.dealers.updateDealerStatus(dealerId, newStatus);
      await loadDealers();
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSalesRepChange = async (dealerId: string, value: string) => {
    try {
      setActionLoading(dealerId);
      if (value === 'house') {
        // House account = no sales rep, no commission
        await apiRequest(`/users/dealer-portal/${dealerId}/sales-rep`, { method: 'DELETE' });
        await apiRequest(`/dealers/${dealerId}`, { method: 'PATCH', body: JSON.stringify({ account_type: 'house' }) });
      } else if (value) {
        // Assigning a sales rep = direct account (commission eligible)
        await apiRequest(`/users/dealer-portal/${dealerId}/assign-sales-rep`, {
          method: 'POST',
          body: JSON.stringify({ sales_rep_id: value }),
        });
        await apiRequest(`/dealers/${dealerId}`, { method: 'PATCH', body: JSON.stringify({ account_type: 'direct' }) });
      } else {
        await apiRequest(`/users/dealer-portal/${dealerId}/sales-rep`, { method: 'DELETE' });
      }
      await loadDealers();
      setEditingSalesRep(null);
    } catch (error) {
      console.error('Failed to assign sales rep:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreditLimitChange = async (dealerId: string, creditLimit: number) => {
    try {
      setActionLoading(dealerId);
      await apiRequest(`/dealer-credit/admin/${dealerId}/settings`, {
        method: 'PATCH',
        body: JSON.stringify({ creditLimit, netAccountEnabled: creditLimit > 0 }),
      });
      await loadDealers();
      setEditingCreditLimit(null);
    } catch (error) {
      console.error('Failed to update credit limit:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const openCreditSettings = async (dealerId: string) => {
    setCreditLoadingId(dealerId);
    setCreditError(null);
    setStarterKitStatus(null);
    try {
      const [settings, skStatus] = await Promise.all([
        apiRequest(`/dealer-credit/admin/${dealerId}`),
        apiRequest(`/dealers/${dealerId}/starter-kit-status`).catch(() => null),
      ]);
      if (!settings || typeof settings !== 'object') {
        throw new Error('Invalid response from server');
      }
      setCreditSettings(settings);
      setCreditLimitValue(settings.creditLimit || 0);
      setCreditNetDays(settings.netTermsDays || 30);
      setCreditEnabled(settings.netAccountEnabled || false);
      setStarterKitStatus(skStatus);
      setShowCreditModal(dealerId);
    } catch (error: any) {
      console.error('Failed to load credit settings:', error);
      setCreditError(`Failed to load credit settings: ${error.message || 'Unknown error'}`);
    } finally {
      setCreditLoadingId(null);
    }
  };

  const saveCreditSettings = async () => {
    if (!showCreditModal) return;
    try {
      setActionLoading(showCreditModal);
      await apiRequest(`/dealer-credit/admin/${showCreditModal}/settings`, {
        method: 'PATCH',
        body: JSON.stringify({
          netAccountEnabled: creditEnabled,
          creditLimit: creditLimitValue,
          netTermsDays: creditNetDays,
        }),
      });
      await loadDealers();
      setShowCreditModal(null);
      setCreditSettings(null);
    } catch (error) {
      console.error('Failed to save credit settings:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleFreezeToggle = async (dealerId: string, isFrozen: boolean) => {
    try {
      setActionLoading(dealerId);
      if (isFrozen) {
        await apiRequest(`/dealer-credit/admin/${dealerId}/unfreeze`, { method: 'POST' });
      } else {
        await apiRequest(`/dealer-credit/admin/${dealerId}/freeze`, {
          method: 'POST',
          body: JSON.stringify({ reason: 'Admin manual freeze' }),
        });
      }
      // Refresh the modal
      const settings = await apiRequest(`/dealer-credit/admin/${dealerId}`);
      setCreditSettings(settings);
      await loadDealers();
    } catch (error) {
      console.error('Failed to toggle freeze:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkKitPurchased = async () => {
    if (!showCreditModal) return;
    try {
      setStarterKitLoading(true);
      await apiRequest(`/dealers/${showCreditModal}/starter-kit-purchased`, { method: 'POST' });
      const skStatus = await apiRequest(`/dealers/${showCreditModal}/starter-kit-status`);
      setStarterKitStatus(skStatus);
      await loadDealers();
    } catch (error: any) {
      console.error('Failed to mark kit purchased:', error);
      setCreditError(error.message || 'Failed to mark kit purchased');
    } finally {
      setStarterKitLoading(false);
    }
  };

  const handleIssueStarterKitCredit = async () => {
    if (!showCreditModal) return;
    try {
      setStarterKitLoading(true);
      await apiRequest(`/dealers/${showCreditModal}/starter-kit-credit`, { method: 'POST' });
      const [skStatus, settings] = await Promise.all([
        apiRequest(`/dealers/${showCreditModal}/starter-kit-status`),
        apiRequest(`/dealer-credit/admin/${showCreditModal}`),
      ]);
      setStarterKitStatus(skStatus);
      setCreditSettings(settings);
      await loadDealers();
    } catch (error: any) {
      console.error('Failed to issue starter kit credit:', error);
      setCreditError(error.message || 'Failed to issue credit');
    } finally {
      setStarterKitLoading(false);
    }
  };

  const openBrandingModal = (dealer: any) => {
    setBrandingEmail(dealer.branded_email || '');
    setShowBrandingModal(dealer.id);
  };

  const handleSaveBranding = async () => {
    if (!showBrandingModal) return;
    try {
      setBrandingLoading(true);
      await apiRequest(`/dealers/${showBrandingModal}/branded-email`, {
        method: 'PATCH',
        body: JSON.stringify({ brandedEmail: brandingEmail || null }),
      });
      setShowBrandingModal(null);
      loadDealers();
    } catch (error) {
      console.error('Failed to save branded email:', error);
    } finally {
      setBrandingLoading(false);
    }
  };

  const getSalesRepName = (dealer: any) => {
    // Rep firm dealers show the firm name
    const firmName = dealer.repFirmName || dealer.rep_firm_name;
    if (firmName) return firmName;
    // Internal sales rep
    const repId = dealer.sales_rep_id;
    if (repId) {
      const rep = salesReps.find(r => r.id === repId);
      return rep ? `${rep.first_name} ${rep.last_name}` : 'Unknown';
    }
    return 'House Account';
  };

  const filteredDealers = dealers.filter(dealer => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      (dealer.name || '').toLowerCase().includes(q) ||
      (dealer.email || '').toLowerCase().includes(q) ||
      (dealer.phone || '').toLowerCase().includes(q) ||
      (dealer.address?.city || '').toLowerCase().includes(q) ||
      (dealer.address?.state || '').toLowerCase().includes(q) ||
      (dealer.address?.zip || '').toLowerCase().includes(q) ||
      (dealer.contact?.firstName && dealer.contact?.lastName &&
        `${dealer.contact.firstName} ${dealer.contact.lastName}`.toLowerCase().includes(q));

    const matchesSalesRep = !salesRepFilter ||
      (salesRepFilter === 'unassigned' ? !dealer.sales_rep_id : dealer.sales_rep_id === salesRepFilter);

    const matchesAccountType = accountTypeTab === 'all' ||
      (accountTypeTab === 'house' && (dealer.accountType || dealer.account_type || 'house') === 'house') ||
      (accountTypeTab === 'rep' && ((dealer.accountType || dealer.account_type) === 'rep' || dealer.repFirmId || dealer.rep_firm_id)) ||
      (accountTypeTab === 'direct' && (dealer.accountType || dealer.account_type) === 'direct');

    const hasFlatRate = (dealer.flatRateShippingPerScreen ?? dealer.flat_rate_shipping_per_screen) != null;
    const matchesFlatRate = !flatRateFilter ||
      (flatRateFilter === 'flat' && hasFlatRate) ||
      (flatRateFilter === 'standard' && !hasFlatRate);

    const dealerState = normalizeStateCode(dealer.address?.state || (dealer as any).state);
    const matchesState = !stateFilter || dealerState === stateFilter;

    const dealerRepFirmId = (dealer as any).repFirmId || (dealer as any).rep_firm_id || '';
    const matchesRepFirm = !repFirmFilter ||
      (repFirmFilter === 'unassigned'
        ? !dealerRepFirmId
        : dealerRepFirmId === repFirmFilter);

    const matchesCounty = !countyFilter || dealerFips[dealer.id] === countyFilter.fips;

    return matchesSearch && matchesSalesRep && matchesAccountType && matchesFlatRate && matchesState && matchesRepFirm && matchesCounty;
  });

  // FIPS → count of in-state dealers (no list filters applied — the map
  // should always show all dealers in the zoomed state, regardless of
  // which county is currently selected for the list).
  const countsByFips: Record<string, number> = {};
  if (zoomState) {
    for (const d of dealers) {
      if (normalizeStateCode((d as any).state || d.address?.state) !== zoomState) continue;
      const fips = dealerFips[d.id];
      if (fips) countsByFips[fips] = (countsByFips[fips] || 0) + 1;
    }
  }

  // Counts-by-state (respects every active filter EXCEPT the state filter itself,
  // so toggling states doesn't erase the picture you're navigating).
  const countsByState = dealers.reduce<Record<string, number>>((acc, dealer) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      (dealer.name || '').toLowerCase().includes(q) ||
      (dealer.email || '').toLowerCase().includes(q) ||
      (dealer.phone || '').toLowerCase().includes(q) ||
      (dealer.address?.city || '').toLowerCase().includes(q) ||
      (dealer.address?.state || '').toLowerCase().includes(q) ||
      (dealer.address?.zip || '').toLowerCase().includes(q);
    const matchesSalesRep = !salesRepFilter ||
      (salesRepFilter === 'unassigned' ? !dealer.sales_rep_id : dealer.sales_rep_id === salesRepFilter);
    const matchesAccountType = accountTypeTab === 'all' ||
      (accountTypeTab === 'house' && (dealer.accountType || dealer.account_type || 'house') === 'house') ||
      (accountTypeTab === 'rep' && ((dealer.accountType || dealer.account_type) === 'rep' || dealer.repFirmId || dealer.rep_firm_id)) ||
      (accountTypeTab === 'direct' && (dealer.accountType || dealer.account_type) === 'direct');
    const dealerRepFirmId = (dealer as any).repFirmId || (dealer as any).rep_firm_id || '';
    const matchesRepFirm = !repFirmFilter ||
      (repFirmFilter === 'unassigned' ? !dealerRepFirmId : dealerRepFirmId === repFirmFilter);

    if (!matchesSearch || !matchesSalesRep || !matchesAccountType || !matchesRepFirm) return acc;
    const state = normalizeStateCode(dealer.address?.state || (dealer as any).state);
    if (state) acc[state] = (acc[state] || 0) + 1;
    return acc;
  }, {});

  // Assign deterministic palette colors by index so the same owner gets the
  // same color across re-renders.
  const repFirmOwners: TerritoryOwner[] = territoryCoverage.repFirms.map((rf, i) => ({
    id: rf.id,
    name: rf.name,
    stateCodes: rf.stateCodes,
    color: OWNER_COLOR_PALETTE[i % OWNER_COLOR_PALETTE.length],
  }));
  const salesRepOwners: TerritoryOwner[] = territoryCoverage.salesReps.map((sr, i) => ({
    id: sr.id,
    name: sr.name,
    stateCodes: sr.stateCodes,
    color: OWNER_COLOR_PALETTE[i % OWNER_COLOR_PALETTE.length],
  }));

  const stats = {
    total: dealers.length,
    active: dealers.filter(d => d.status === 'active').length,
    pending: dealers.filter(d => d.status === 'pending').length,
    inactive: dealers.filter(d => d.status === 'inactive').length,
    unassigned: dealers.filter(d => !d.sales_rep_id).length,
    house: dealers.filter(d => (d.accountType || d.account_type || 'house') === 'house').length,
    rep: dealers.filter(d => (d.accountType || d.account_type) === 'rep' || d.repFirmId || d.rep_firm_id).length,
    direct: dealers.filter(d => (d.accountType || d.account_type) === 'direct').length,
  };

  return (
    <div>
      <PageHeader
        title="Dealers"
        subtitle="Manage dealer accounts and sales rep assignments"
        action={
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Add Dealer
          </button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-500">Total Dealers</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-blue-600">{dealerUserCount}</p>
          <p className="text-sm text-gray-500">Dealer Users</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-green-600">{stats.active}</p>
          <p className="text-sm text-gray-500">Active</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
          <p className="text-sm text-gray-500">Pending</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-gray-400">{stats.inactive}</p>
          <p className="text-sm text-gray-500">Inactive</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-orange-600">{stats.unassigned}</p>
          <p className="text-sm text-gray-500">No Sales Rep</p>
        </Card>
      </div>

      {/* Account Type Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4 w-fit">
        {([
          { key: 'all' as const, label: 'All Dealers', count: stats.total },
          { key: 'house' as const, label: 'House', count: stats.house, color: 'text-gray-600' },
          { key: 'direct' as const, label: 'Direct (Sales Rep)', count: stats.direct, color: 'text-blue-600' },
          { key: 'rep' as const, label: 'Rep Firm', count: stats.rep, color: 'text-purple-600' },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setAccountTypeTab(tab.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              accountTypeTab === tab.key
                ? 'bg-white shadow text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label} <span className={`ml-1 ${tab.color || ''}`}>({tab.count})</span>
          </button>
        ))}
      </div>

      {/* Map (collapsible) */}
      <Card className="mb-4">
        <div className="flex items-start justify-between mb-3 gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">
              Dealer Footprint
              {zoomState && <span className="ml-2 text-blue-600">· {zoomState} counties</span>}
            </h3>
            <p className="text-xs text-gray-500">
              {zoomState
                ? `Zoomed into ${zoomState}. Click "Back to US" to return.`
                : 'Click a state to zoom in. Click a state on the dropdown below to filter the list.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {showMap && (
              <div className="flex gap-0.5 bg-gray-100 rounded-md p-0.5 text-xs">
                {([
                  { key: 'density' as const, label: 'Dealer Density' },
                  { key: 'repFirm' as const, label: 'Rep Firm Territories' },
                  { key: 'salesRep' as const, label: 'Sales Rep Territories' },
                ]).map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setMapMode(opt.key)}
                    className={`px-3 py-1 rounded font-medium transition-colors ${
                      mapMode === opt.key
                        ? 'bg-white shadow text-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            {zoomState && (
              <button
                onClick={() => setZoomState('')}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
              >
                ← Back to US
              </button>
            )}
            <button
              onClick={() => setShowMap(!showMap)}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap"
            >
              {showMap ? 'Hide map' : 'Show map'}
            </button>
          </div>
        </div>
        {showMap && (
          zoomState ? (
            <>
              {geocodingState === zoomState && (
                <div className="mb-2 text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-3 py-1.5">
                  Geocoding dealer ZIPs for {zoomState}…
                </div>
              )}
              {countyFilter && (
                <div className="mb-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-1.5 flex justify-between items-center">
                  <span>Showing dealers in <strong>{countyFilter.name}, {zoomState}</strong></span>
                  <button onClick={() => setCountyFilter(null)} className="text-blue-600 hover:text-blue-800 font-medium">
                    Clear county filter
                  </button>
                </div>
              )}
              <UsCountyMap
                visibleStates={[zoomState]}
                owners={[]}
                selectedFips={countyFilter?.fips || null}
                countsByFips={countsByFips}
                onCountyClick={(fips, name) => {
                  // Toggle: clicking the same county clears the filter
                  setCountyFilter(prev => prev?.fips === fips ? null : { fips, name });
                }}
              />
            </>
          ) : (
            <UsStateMap
              mode={mapMode}
              countsByState={countsByState}
              owners={mapMode === 'repFirm' ? repFirmOwners : mapMode === 'salesRep' ? salesRepOwners : []}
              selectedState={stateFilter}
              onStateSelect={(code) => {
                // Click toggles list filter AND zooms into county view.
                // Clicking the same state again clears both.
                if (code === stateFilter) {
                  setStateFilter('');
                  setZoomState('');
                } else {
                  setStateFilter(code);
                  if (code) setZoomState(code);
                }
              }}
            />
          )
        )}
      </Card>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search by name, email, city, state, zip..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              title="Filter by dealer state"
            >
              <option value="">All States</option>
              {Object.keys(countsByState)
                .filter(s => s)
                .sort()
                .map(state => (
                  <option key={state} value={state}>
                    {state} ({countsByState[state]})
                  </option>
                ))}
            </select>
          </div>
          <div>
            <select
              value={salesRepFilter}
              onChange={(e) => setSalesRepFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Sales Reps</option>
              <option value="unassigned">House Account</option>
              {salesReps.map(rep => (
                <option key={rep.id} value={rep.id}>
                  {rep.first_name} {rep.last_name}
                </option>
              ))}
            </select>
          </div>
          {accountTypeTab === 'rep' && (
            <div>
              <select
                value={repFirmFilter}
                onChange={(e) => setRepFirmFilter(e.target.value)}
                className="w-full px-4 py-2 border border-purple-300 bg-purple-50 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                title="Filter by rep firm"
              >
                <option value="">All Rep Firms</option>
                <option value="unassigned">Unassigned</option>
                {repFirms
                  .slice()
                  .sort((a, b) => (a.name || a.firm_name || '').localeCompare(b.name || b.firm_name || ''))
                  .map((firm: any) => (
                    <option key={firm.id} value={firm.id}>
                      {firm.name || firm.firm_name}
                    </option>
                  ))}
              </select>
            </div>
          )}
          <div>
            <select
              value={flatRateFilter}
              onChange={(e) => setFlatRateFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              title="Filter by flat-rate shipping configuration"
            >
              <option value="">All Shipping</option>
              <option value="flat">Flat-Rate Only</option>
              <option value="standard">Standard Only</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Error Toast */}
      {creditError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center justify-between">
          <span className="text-sm">{creditError}</span>
          <button onClick={() => setCreditError(null)} className="text-red-400 hover:text-red-600 ml-4">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Dealers Table */}
      <Card>
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dealer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales Rep
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credit Limit
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credit Used
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orders
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDealers.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <p className="text-lg font-medium">No dealers found</p>
                        <p className="text-sm mt-1">
                          {statusFilter ? `No dealers with status "${statusFilter}"` : 'Create a dealer to get started'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
                {filteredDealers.map(dealer => (
                  <tr key={dealer.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/dealers/${dealer.id}`}
                            className="font-medium text-blue-600 hover:text-blue-800"
                          >
                            {dealer.name}
                          </Link>
                          {/* Tier Badge */}
                          {dealer.rewards?.tierIcon && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded-full"
                              style={{ backgroundColor: `${dealer.rewards?.tierColor}20`, color: dealer.rewards?.tierColor }}
                              title={`${dealer.rewards?.currentPoints?.toLocaleString()} points`}
                            >
                              {dealer.rewards?.tierIcon}
                            </span>
                          )}
                          {/* Branding Badges */}
                          {dealer.logo_url && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700" title="White-labeled">
                              WL
                            </span>
                          )}
                          {dealer.branded_email && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700" title={`Branded: ${dealer.branded_email}`}>
                              @
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{dealer.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {dealer.address?.city || '-'}, {dealer.address?.state || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {editingSalesRep === dealer.id ? (
                        <select
                          className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                          defaultValue={dealer.sales_rep_id || 'house'}
                          onChange={(e) => handleSalesRepChange(dealer.id, e.target.value)}
                          onBlur={() => setEditingSalesRep(null)}
                          autoFocus
                        >
                          <option value="house">House Account</option>
                          {salesReps.map(rep => (
                            <option key={rep.id} value={rep.id}>
                              {rep.first_name} {rep.last_name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <button
                          onClick={() => setEditingSalesRep(dealer.id)}
                          className={`text-sm px-2 py-1 rounded ${
                            (dealer.repFirmName || dealer.rep_firm_name)
                              ? 'text-purple-700 hover:bg-purple-50 font-medium'
                              : dealer.sales_rep_id
                              ? 'text-blue-600 hover:bg-blue-50'
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                          disabled={actionLoading === dealer.id}
                        >
                          {actionLoading === dealer.id ? '...' : getSalesRepName(dealer)}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <StatusBadge status={dealer.status} type="dealer" />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {dealer.discountPercent}%
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {editingCreditLimit === dealer.id ? (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-400">$</span>
                          <input
                            type="number"
                            className="w-24 text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                            value={creditLimitValue}
                            onChange={(e) => setCreditLimitValue(Number(e.target.value))}
                            onWheel={(e) => e.currentTarget.blur()}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleCreditLimitChange(dealer.id, creditLimitValue);
                              } else if (e.key === 'Escape') {
                                setEditingCreditLimit(null);
                              }
                            }}
                          />
                          <button
                            onClick={() => handleCreditLimitChange(dealer.id, creditLimitValue)}
                            className="text-green-600 hover:text-green-800"
                            disabled={actionLoading === dealer.id}
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setEditingCreditLimit(null)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditingCreditLimit(dealer.id);
                              setCreditLimitValue(dealer.credit_limit || 0);
                            }}
                            className={`text-sm px-2 py-1 rounded ${
                              dealer.credit_limit > 0
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-400 hover:bg-gray-50'
                            }`}
                            disabled={actionLoading === dealer.id}
                          >
                            {dealer.credit_limit > 0
                              ? `$${dealer.credit_limit.toLocaleString()}`
                              : 'Set Limit'}
                          </button>
                          <button
                            onClick={() => openCreditSettings(dealer.id)}
                            className={`p-1 ${creditLoadingId === dealer.id ? 'text-blue-500 animate-spin' : 'text-gray-400 hover:text-blue-600'}`}
                            title="Credit Account Settings"
                            disabled={creditLoadingId === dealer.id}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          {dealer.net_account_enabled && (
                            <span className={`w-2 h-2 rounded-full ${
                              dealer.credit_account_status === 'active' ? 'bg-green-500' :
                              dealer.credit_account_status === 'frozen' ? 'bg-yellow-500' : 'bg-red-500'
                            }`} title={`Net account: ${dealer.credit_account_status}`} />
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {dealer.credit_limit > 0 ? (
                        <span className={`font-medium ${
                          dealer.credit_balance >= dealer.credit_limit
                            ? 'text-red-600'
                            : dealer.credit_balance > dealer.credit_limit * 0.8
                            ? 'text-yellow-600'
                            : 'text-gray-900'
                        }`}>
                          ${(dealer.credit_balance || 0).toLocaleString()}
                          <span className="text-gray-400 text-xs ml-1">
                            / ${dealer.credit_limit.toLocaleString()}
                          </span>
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {dealer.totalOrders || 0}
                    </td>
                    <td className="px-2 py-3 text-xs">
                      <div className="flex items-center flex-wrap gap-1">
                        <button
                          onClick={() => setContactCardDealer(dealer)}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Contact
                        </button>
                        <Link
                          to={`/dealers/${dealer.id}`}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          Details
                        </Link>
                        <button
                          onClick={() => openBrandingModal(dealer)}
                          className="text-purple-600 hover:text-purple-800"
                          title="White-label branding"
                        >
                          Brand
                        </button>
                        {dealer.status === 'active' && (
                          <div className="relative">
                            <button
                              onClick={async () => {
                                if (impersonateMenu === dealer.id) {
                                  setImpersonateMenu(null);
                                  setTeamData(null);
                                  return;
                                }
                                setImpersonateMenu(dealer.id);
                                setTeamLoading(true);
                                try {
                                  const team = await api.dealers.getDealerTeam(dealer.id);
                                  setTeamData(team);
                                } catch {
                                  setTeamData({ contacts: [], salesReps: [] });
                                } finally {
                                  setTeamLoading(false);
                                }
                              }}
                              className="text-amber-600 hover:text-amber-800"
                              title="Login as this dealer"
                            >
                              Login As ▾
                            </button>
                            {impersonateMenu === dealer.id && (
                              <div className="absolute right-0 top-6 z-50 bg-white border border-gray-200 rounded-lg shadow-xl min-w-[220px] py-1">
                                {teamLoading ? (
                                  <div className="px-4 py-2 text-sm text-gray-500">Loading team...</div>
                                ) : (
                                  <>
                                    {teamData?.contacts.length > 0 && (
                                      <div className="px-4 py-1 text-[10px] font-semibold text-gray-400 uppercase">Portal Users</div>
                                    )}
                                    {teamData?.contacts.map(c => (
                                      <button
                                        key={c.id}
                                        onClick={async () => {
                                          try {
                                            const result = await api.dealers.impersonateDealer(dealer.id, c.id);
                                            const dealerUrl = window.location.hostname.includes('kederscreens.com')
                                              ? 'https://dealerportal.kederscreens.com'
                                              : `${window.location.protocol}//${window.location.hostname}:3001`;
                                            window.open(`${dealerUrl}/impersonate?token=${result.token}`, '_blank');
                                          } catch (err: any) {
                                            alert(err.message || 'Failed to impersonate');
                                          }
                                          setImpersonateMenu(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex flex-col"
                                      >
                                        <div className="flex items-center gap-2">
                                          <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded ${c.isAdmin ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                                            {c.isAdmin ? 'ADMIN' : 'USER'}
                                          </span>
                                          <span className="font-medium">{c.name}</span>
                                        </div>
                                        <span className="text-[11px] text-gray-400 ml-[34px]">{c.email}</span>
                                      </button>
                                    ))}
                                    {teamData?.salesReps && teamData.salesReps.length > 0 && (
                                      <>
                                        {(teamData?.contacts?.length || 0) > 0 && <hr className="my-1 border-gray-100" />}
                                        <div className="px-4 py-1 text-[10px] font-semibold text-gray-400 uppercase">Sales Team</div>
                                        {teamData.salesReps.map(r => (
                                          <button
                                            key={r.id}
                                            onClick={async () => {
                                              try {
                                                const result = await api.dealers.impersonateSalesRep(r.id);
                                                const dealerUrl = window.location.hostname.includes('kederscreens.com')
                                                  ? 'https://dealerportal.kederscreens.com'
                                                  : `${window.location.protocol}//${window.location.hostname}:3001`;
                                                window.open(`${dealerUrl}/impersonate?token=${result.token}`, '_blank');
                                              } catch (err: any) {
                                                alert(err.message || 'Failed to impersonate');
                                              }
                                              setImpersonateMenu(null);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                                          >
                                            <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded ${r.role === 'sales_manager' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                                              {r.role === 'sales_manager' ? 'MGR' : 'REP'}
                                            </span>
                                            <span>{r.name}</span>
                                          </button>
                                        ))}
                                      </>
                                    )}
                                    {(!teamData?.contacts?.length && !teamData?.salesReps?.length) && (
                                      <div className="px-4 py-2 text-sm text-gray-400">No team members</div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {dealer.status === 'pending' && (
                          <button
                            onClick={() => handleStatusChange(dealer.id, 'active')}
                            disabled={actionLoading === dealer.id}
                            className="text-green-600 hover:text-green-800 disabled:opacity-50"
                          >
                            {actionLoading === dealer.id ? '...' : 'Approve'}
                          </button>
                        )}
                        {dealer.status === 'active' && (
                          <button
                            onClick={() => handleStatusChange(dealer.id, 'inactive')}
                            disabled={actionLoading === dealer.id}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            {actionLoading === dealer.id ? '...' : 'Deactivate'}
                          </button>
                        )}
                        {dealer.status === 'inactive' && (
                          <>
                            <button
                              onClick={() => handleStatusChange(dealer.id, 'active')}
                              disabled={actionLoading === dealer.id}
                              className="text-green-600 hover:text-green-800 disabled:opacity-50"
                            >
                              {actionLoading === dealer.id ? '...' : 'Reactivate'}
                            </button>
                            {(dealer.totalOrders || 0) === 0 && (
                              <button
                                onClick={async () => {
                                  if (!confirm(`Delete "${dealer.company_name || dealer.companyName}"? This cannot be undone.`)) return;
                                  try {
                                    setActionLoading(dealer.id);
                                    await api.dealers.deleteDealer(dealer.id);
                                    await loadDealers();
                                  } catch (err: any) {
                                    alert(err.message || 'Failed to delete dealer');
                                  } finally {
                                    setActionLoading(null);
                                  }
                                }}
                                disabled={actionLoading === dealer.id}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                              >
                                {actionLoading === dealer.id ? '...' : 'Delete'}
                              </button>
                            )}
                          </>
                        )}
                        <button
                          onClick={() => { setShowRepFirmModal(dealer.id); setSelectedRepFirm(dealer.repFirmId || dealer.rep_firm_id || ''); }}
                          className={`text-xs px-2 py-0.5 rounded ${
                            (dealer.repFirmId || dealer.rep_firm_id)
                              ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          title="Assign to rep firm"
                        >
                          {(dealer.repFirmId || dealer.rep_firm_id) ? 'Rep Firm' : '+ Rep Firm'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Rep Firm Assignment Modal */}
      {showRepFirmModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Assign to Rep Firm</h3>
            <p className="text-sm text-gray-500 mb-4">
              Assigning a dealer to a rep firm sets their account type to "rep" and discount to 50%.
              Removing from a rep firm sets them back to "house".
            </p>
            <select
              value={selectedRepFirm}
              onChange={(e) => setSelectedRepFirm(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm mb-4 focus:ring-2 focus:ring-purple-500 focus:outline-none"
            >
              <option value="">No Rep Firm (House Account)</option>
              {repFirms.map((f: any) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRepFirmModal(null)}
                className="flex-1 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    setActionLoading(showRepFirmModal);
                    const firmId = selectedRepFirm || null;
                    const accountType = firmId ? 'rep' : 'house';
                    const discountPercent = firmId ? 50 : undefined;
                    const updateData: any = { account_type: accountType };
                    if (firmId) {
                      // Assign to rep firm via direct SQL since updateDealer doesn't handle rep_firm_id
                      await apiRequest(`/rep-firms/${firmId}`, { method: 'GET' }); // verify firm exists
                    }
                    // Use raw PATCH to set rep_firm_id + account_type + discount
                    await apiRequest(`/dealers/${showRepFirmModal}/rep-firm-assign`, {
                      method: 'POST',
                      body: JSON.stringify({ repFirmId: firmId }),
                    });
                    setShowRepFirmModal(null);
                    await loadDealers();
                  } catch (err: any) {
                    alert(err.message || 'Failed to assign');
                  } finally {
                    setActionLoading(null);
                  }
                }}
                className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Dealer Modal */}
      {showCreateModal && (
        <CreateDealerModal
          salesReps={salesReps}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadDealers();
          }}
        />
      )}

      {/* Credit Settings Modal */}
      {showCreditModal && creditSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Net Credit Account</h3>
              <button onClick={() => { setShowCreditModal(null); setCreditSettings(null); setStarterKitStatus(null); }} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Account Status */}
            <div className="mb-4 p-3 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Account Status</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  creditSettings.accountStatus === 'active' ? 'bg-green-100 text-green-700' :
                  creditSettings.accountStatus === 'frozen' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {creditSettings.accountStatus}
                </span>
              </div>
              {creditSettings.netAccountEnabled && (
                <div className="text-sm text-gray-600">
                  <div className="flex justify-between"><span>Balance:</span><span className="font-medium">${creditSettings.currentBalance?.toLocaleString() || '0'}</span></div>
                  <div className="flex justify-between"><span>Available:</span><span className="font-medium text-green-600">${creditSettings.creditAvailable?.toLocaleString() || '0'}</span></div>
                  {creditSettings.hasOverdueInvoices && (
                    <div className="mt-1 text-xs text-red-600 font-medium">Has overdue invoices</div>
                  )}
                </div>
              )}
            </div>

            {/* Enable Toggle */}
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-medium text-gray-700">Enable Net Account</label>
              <button
                onClick={() => setCreditEnabled(!creditEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${creditEnabled ? 'bg-green-600' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${creditEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {/* Credit Limit */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Credit Limit ($)</label>
              <input
                type="number"
                value={creditLimitValue}
                onChange={(e) => setCreditLimitValue(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
                step="500"
              />
            </div>

            {/* Net Terms */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Net Terms</label>
              <select
                value={creditNetDays}
                onChange={(e) => setCreditNetDays(parseInt(e.target.value))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={15}>Net 15</option>
                <option value={30}>Net 30</option>
                <option value={45}>Net 45</option>
                <option value={60}>Net 60</option>
              </select>
            </div>

            {/* Freeze/Unfreeze */}
            {creditSettings.netAccountEnabled && (
              <div className="mb-4">
                <button
                  onClick={() => handleFreezeToggle(showCreditModal, creditSettings.accountStatus === 'frozen' || creditSettings.accountStatus === 'suspended')}
                  className={`w-full px-3 py-2 rounded-lg text-sm font-medium ${
                    creditSettings.accountStatus === 'active'
                      ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-300'
                      : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-300'
                  }`}
                >
                  {creditSettings.accountStatus === 'active' ? 'Freeze Account' : 'Unfreeze Account'}
                </button>
              </div>
            )}

            {/* Starter Kit Credit */}
            {starterKitStatus && (
              <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50">
                <h4 className="text-sm font-semibold text-amber-800 mb-2">Starter Kit $350 Credit</h4>
                {starterKitStatus.creditIssued ? (
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>Credit Issued {starterKitStatus.creditIssuedAt ? new Date(starterKitStatus.creditIssuedAt).toLocaleDateString() : ''}</span>
                  </div>
                ) : !starterKitStatus.purchased ? (
                  <div>
                    <p className="text-xs text-gray-600 mb-2">
                      Mark when this dealer purchases their first Starter Kit. They'll earn a $350 credit after reaching $7,500 in sales.
                    </p>
                    <button
                      onClick={handleMarkKitPurchased}
                      disabled={starterKitLoading}
                      className="w-full px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                    >
                      {starterKitLoading ? 'Saving...' : 'Mark Kit Purchased'}
                    </button>
                  </div>
                ) : (
                  <div>
                    {/* Sales Progress */}
                    <div className="mb-2">
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Sales: ${starterKitStatus.totalSales?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        <span>Goal: $7,500</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${starterKitStatus.eligible ? 'bg-green-500' : 'bg-amber-500'}`}
                          style={{ width: `${Math.min((starterKitStatus.totalSales / 7500) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                      Kit purchased {starterKitStatus.purchasedAt ? new Date(starterKitStatus.purchasedAt).toLocaleDateString() : ''}
                    </p>
                    {starterKitStatus.eligible ? (
                      <button
                        onClick={handleIssueStarterKitCredit}
                        disabled={starterKitLoading}
                        className="w-full px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        {starterKitLoading ? 'Issuing...' : 'Issue $350 Credit'}
                      </button>
                    ) : (
                      <p className="text-xs text-amber-700 font-medium">
                        ${(7500 - starterKitStatus.totalSales).toLocaleString(undefined, { maximumFractionDigits: 0 })} more in sales needed
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowCreditModal(null); setCreditSettings(null); setStarterKitStatus(null); }}
                className="flex-1 px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveCreditSettings}
                disabled={actionLoading === showCreditModal}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading === showCreditModal ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Branding Modal */}
      {showBrandingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">White-Label Branding</h3>
              <button onClick={() => setShowBrandingModal(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Set a branded email for this dealer. Create the shared mailbox in Microsoft 365 first, then enter the address here.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Branded Email Address</label>
              <input
                type="email"
                value={brandingEmail}
                onChange={(e) => setBrandingEmail(e.target.value)}
                placeholder="dealername@kederscreens.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Must be a shared mailbox with send-as permissions granted to orders@kederscreens.com
              </p>
            </div>

            {/* Current status */}
            {(() => {
              const dealer = dealers.find(d => d.id === showBrandingModal);
              return (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-2 h-2 rounded-full ${dealer?.logo_url ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span>{dealer?.logo_url ? 'Logo uploaded' : 'No logo (dealer needs to upload)'}</span>
                  </div>
                  {dealer?.logo_url && (
                    <div className="mb-2 flex justify-center">
                      <img src={dealer.logo_url} alt="Dealer logo" className="h-12 max-w-[200px] object-contain rounded border bg-white p-1" />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${dealer?.branded_email ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span>{dealer?.branded_email ? `Email: ${dealer.branded_email}` : 'No branded email'}</span>
                  </div>
                </div>
              );
            })()}

            <div className="flex gap-3">
              <button
                onClick={() => setShowBrandingModal(null)}
                className="flex-1 px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              {brandingEmail && (
                <button
                  onClick={() => { setBrandingEmail(''); }}
                  className="px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  Clear
                </button>
              )}
              <button
                onClick={handleSaveBranding}
                disabled={brandingLoading}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {brandingLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Card Modal */}
      {contactCardDealer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setContactCardDealer(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl p-5 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">{contactCardDealer.name}</h3>
                  {contactCardDealer.contact?.firstName && (
                    <p className="text-blue-100 text-sm mt-0.5">
                      {contactCardDealer.contact.firstName} {contactCardDealer.contact.lastName}
                    </p>
                  )}
                </div>
                <button onClick={() => setContactCardDealer(null)} className="text-white/70 hover:text-white">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <StatusBadge status={contactCardDealer.status} type="dealer" />
                <span className="text-blue-200 text-xs">{contactCardDealer.discountPercent}% discount</span>
              </div>
            </div>

            {/* Contact Details */}
            <div className="p-5 space-y-4">
              {/* Phone Numbers */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Phone</p>
                <div className="space-y-1.5">
                  {contactCardDealer.phone && (
                    <a href={`tel:${contactCardDealer.phone}`} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {contactCardDealer.phone} <span className="text-gray-400 text-xs">Main</span>
                    </a>
                  )}
                  {contactCardDealer.contact?.phoneWork && (
                    <a href={`tel:${contactCardDealer.contact.phoneWork}`} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {contactCardDealer.contact.phoneWork} <span className="text-gray-400 text-xs">Work</span>
                    </a>
                  )}
                  {contactCardDealer.contact?.phoneMobile && (
                    <a href={`tel:${contactCardDealer.contact.phoneMobile}`} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      {contactCardDealer.contact.phoneMobile} <span className="text-gray-400 text-xs">Mobile</span>
                    </a>
                  )}
                  {!contactCardDealer.phone && !contactCardDealer.contact?.phoneWork && !contactCardDealer.contact?.phoneMobile && (
                    <p className="text-sm text-gray-400 italic">No phone on file</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Email</p>
                {contactCardDealer.email ? (
                  <a href={`mailto:${contactCardDealer.email}`} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {contactCardDealer.email}
                  </a>
                ) : (
                  <p className="text-sm text-gray-400 italic">No email on file</p>
                )}
              </div>

              {/* Address */}
              {(contactCardDealer.address?.city || contactCardDealer.address?.state) && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Location</p>
                  <div className="flex items-start gap-2 text-sm text-gray-700">
                    <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      {contactCardDealer.address.street && <p>{contactCardDealer.address.street}</p>}
                      <p>
                        {[contactCardDealer.address.city, contactCardDealer.address.state].filter(Boolean).join(', ')}
                        {contactCardDealer.address.zip ? ` ${contactCardDealer.address.zip}` : ''}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sales Rep */}
              {contactCardDealer.sales_rep_id && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Sales Rep</p>
                  <p className="text-sm text-gray-700">{getSalesRepName(contactCardDealer)}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-5 py-3 flex justify-between">
              <Link
                to={`/dealers/${contactCardDealer.id}`}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View Full Details
              </Link>
              <button
                onClick={() => setContactCardDealer(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateDealerModal({
  salesReps,
  onClose,
  onCreated,
}: {
  salesReps: SalesRep[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    discountPercent: 35,
    street: '',
    city: '',
    state: '',
    zip: '',
    sales_rep_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      await api.dealers.createDealer({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        discountPercent: formData.discountPercent,
        sales_rep_id: formData.sales_rep_id || undefined,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: 'US',
        },
      });
      onCreated();
    } catch (err) {
      setError('Failed to create dealer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">Add New Dealer</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.discountPercent}
                onChange={(e) => setFormData({ ...formData, discountPercent: parseInt(e.target.value) })}
                onWheel={(e) => e.currentTarget.blur()}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Sales Rep *</label>
              <select
                required
                value={formData.sales_rep_id}
                onChange={(e) => setFormData({ ...formData, sales_rep_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Sales Rep</option>
                {salesReps.map(rep => (
                  <option key={rep.id} value={rep.id}>
                    {rep.first_name} {rep.last_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
            <input
              type="text"
              value={formData.street}
              onChange={(e) => setFormData({ ...formData, street: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
              <input
                type="text"
                value={formData.zip}
                onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Dealer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
